import React, { useState, useEffect, useMemo, useRef } from "react";
import MaterialTable from "material-table";
import { Row, Col } from "react-bootstrap";

import { useFormatter } from "../../../util/formatter";
import Noti from "../../../util/noti";

import "react-datepicker/dist/react-datepicker.css";
import ReceiptModel from "./ReceiptModel";
import ReceiptForm from "./ReceiptForm";
import { useUserProps } from "util/userDBContext";
import {
	isAcctUser,
	isGYTHUser,
	isGYVNUser,
	isITUser,
	isManagementUser,
	isOMUser,
} from "util/roles";
import { useUser } from "util/user";

const ReceiptTable = (props) => {
	const [formatNumber] = useFormatter();
	const { loggedCountry } = useUserProps();
	const user = useUser();

	const [rows, setRows] = useState(null);
	const [selectedReceipt, setSelectedReceipt] = useState(null);
	const [showForm, setShowForm] = useState(false);
	const [formClass, setFormClass] = useState("");
	const [showPreview, setShowPreview] = useState(false);
	const [country, setCountry] = useState(loggedCountry);
	const selectedRows = props.selectedRows || [];
	const setSelectedRows = props.setSelectedRows;
	const matTbRef = useRef(null); // for resetting material table component

	const roles = user.roles;
	const userInfo = props.userInfo;

	// ----------------------- Bulk actions -----------------------
	const userInvSameCountry = useMemo(
		() => country === loggedCountry,
		[country, loggedCountry],
	);
	const isShownBulkActions = useMemo(
		() =>
			(isGYVNUser(roles) && isAcctUser(roles) && userInvSameCountry) ||
			(isGYTHUser(roles) && isAcctUser(roles) && userInvSameCountry) ||
			(isGYTHUser(roles) && isOMUser(roles) && userInvSameCountry) ||
			(isGYTHUser(roles) && isManagementUser(roles) && userInvSameCountry) ||
			isITUser(roles),
		[roles, userInfo, userInvSameCountry],
	);

	const initColumns = [
		{ title: "Month", field: "yearMonth", width: 100 },
		{ title: "Entity", field: "entity", width: 50 },
		{ title: "Client", field: "client", width: 300 },
		{
			title: "Site",
			field: "siteName",
			render: (dataObj) => renderMSA(dataObj),
			width: 300,
		},
		{ title: "Project ID", grouping: false, field: "projectID", width: 120 },
		{ title: "Invoice No.", grouping: false, field: "invoiceNo", width: 200 },
		{
			title: "Amount Ex VAT",
			grouping: false,
			field: "subtotalPrice",
			render: (dataObj) => formatNumber(dataObj?.subtotalPrice || 0, 2),
			width: 150,
		},
		{
			title: "VAT",
			grouping: false,
			field: "VATPrice",
			render: (dataObj) => formatNumber(dataObj?.VATPrice || 0, 2),
			width: 120,
		},
		{
			title: "Total Inc VAT",
			grouping: false,
			field: "totalDue",
			render: (dataObj) => formatNumber(dataObj?.totalDue || 0, 2),
			width: 150,
		},
		{
			title: "Invoice Date",
			grouping: false,
			field: "invoiceDate",
			width: 150,
		},
		{ title: "Credit term", grouping: false, field: "creditTerm", width: 120 },
		{ title: "Due Date", grouping: false, field: "dueDate", width: 150 },
		{
			title: "Payment Date",
			grouping: false,
			field: "paymentDate",
			width: 150,
		},
		{
			title: "Aging Days",
			grouping: false,
			field: "agingDays",
			render: (dataObj) => renderAgingDays(dataObj),
			width: 100,
		},
		{
			title: "Receipt Number",
			grouping: false,
			field: "receiptNo",
			width: 180,
		},
		{
			title: "Receipt Date",
			grouping: false,
			field: "receiptDate",
			render: (dataObj) => renderReceiptDate(dataObj),
			width: 150,
		},
		{
			title: "Actions",
			grouping: false,
			render: (dataObj) => renderActionsCol(dataObj),
			width: 100,
		},
	];

	const [columns, setColumns] = useState(initColumns);

	const tableOpts = {
		showTitle: false,
		selection: isShownBulkActions,
		grouping: true,
		pageSize: 10,
		pageSizeOptions: [10, 20, 40, 60],
		headerStyle: {
			fontSize: "1rem",
			textAlign: "center",
		},
		cellStyle: {
			textAlign: "center",
		},
	};

	const downloadReceiptReportCsv = () => {
		if (!Array.isArray(selectedRows) || selectedRows.length === 0) return;

		const headers = [
			"Month",
			"Entity",
			"Client",
			"Site",
			"Project ID",
			"Invoice No.",
			"Amount Ex VAT",
			"VAT",
			"Total Inc VAT",
			"Invoice Date",
			"Credit term",
			"Due Date",
			"Aging Days",
			"Payment Date",
			"Receipt Number",
			"Receipt Date",
		];

		const toCsvCell = (value) => {
			const raw = value == null ? "" : String(value);
			const escaped = raw.replace(/"/g, '""');
			return `"${escaped}"`;
		};

		const csvRows = [headers.map(toCsvCell).join(",")];

		selectedRows.forEach((row) => {
			const rowValues = [
				row?.yearMonth || "",
				row?.entity || "",
				row?.client || "",
				row?.siteName || "",
				row?.projectID || "",
				row?.invoiceNo || "",
				formatNumber(row?.subtotalPrice || 0, 2),
				formatNumber(row?.VATPrice || 0, 2),
				formatNumber(row?.totalDue || 0, 2),
				row?.invoiceDate || "-",
				row?.creditTerm ?? "",
				row?.dueDate || "-",
				row?.agingDays ?? "",
				row?.paymentDate || "-",
				row?.receiptNo || "-",
				row?.paymentDate || "-",
			];

			csvRows.push(rowValues.map(toCsvCell).join(","));
		});

		const csvContent = csvRows.join("\n");
		const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const anchor = document.createElement("a");
		anchor.href = url;
		anchor.download = "receipt_report.csv";
		document.body.appendChild(anchor);
		anchor.click();
		document.body.removeChild(anchor);
		URL.revokeObjectURL(url);
	};

	/**
	 * Renders button component based on status of the receipt
	 * @param {object} dataObj
	 * @returns Button component
	 */
	const renderActionsCol = (dataObj) => {
		const isPaid = dataObj.paymentDate != "-";
		const isMissingInfo = dataObj.additional_msa_info == null;
		const isMSA = dataObj.isMSA;

		const initBtn = (
			<button
				title="Issue Receipt"
				className="btn btn-generate btn-invoice"
				onClick={() => initForm(dataObj)}
			>
				<span className="glyphicon glyphicon-pencil"></span>
			</button>
		);
		const showPreviewBtn = (
			<button
				title="Preview Receipt"
				className="btn btn-generate btn-report"
				onClick={() => {
					if (!showPreview) setShowPreview(true);

					setSelectedReceipt(dataObj.data);
				}}
			>
				<span className="glyphicon glyphicon-list-alt"></span>
			</button>
		);

		if (isMSA) {
			return !isMissingInfo && !isPaid ? initBtn : showPreviewBtn;
		} else {
			return !isPaid ? initBtn : showPreviewBtn;
		}
	};

	const renderAgingDays = (dataObj) => {
		const agingDaysNo = dataObj.agingDays;
		const isOverDued = agingDaysNo > 0;

		return isOverDued ? (
			<b style={{ color: "red" }}>{agingDaysNo}</b>
		) : (
			agingDaysNo
		);
	};

	const renderMSA = (dataObj) => {
		return dataObj.isMSA ? (
			<p
				style={{ color: "#ff8500", fontSize: "inherit", fontFamily: "inherit" }}
			>
				{dataObj.siteName}
			</p>
		) : (
			<p style={{ fontSize: "inherit", fontFamily: "inherit" }}>
				{dataObj.siteName}
			</p>
		);
	};

	const renderReceiptDate = (dataObj) => {
		const issuedAt = dataObj?.receiptIssuedAt || dataObj?.data?.receipt_issued_at || dataObj?.data?.receipt?.issuedAt;
		return issuedAt ? String(issuedAt).slice(0, 10) : "-";
	};
	/**
	 * Reset preview sites
	 */
	const resetPreview = () => {
		setShowPreview(false);
		setSelectedReceipt(null);
	};

	/**
	 * Reset form to its default state
	 */
	const resetForm = () => {
		setShowForm(false);
		setSelectedReceipt(null);
		setFormClass("fadeout");
	};

	/**
	 * Initialize the form with selected invoice data
	 * @param {object} dataObj
	 */
	const initForm = (dataObj) => {
		setShowPreview(false);
		setShowForm(true);
		setSelectedReceipt(dataObj.data);
		setFormClass("fadeIn");
	};

	/**
	 * Display notifications
	 * @param {object} param0
	 */
	const notify = ({ type, msg }) => {
		const setReload = props.setReload;

		Noti.create({ type, msg });
		setReload(true);
		resetForm();
	};

	useEffect(() => {
		if (isShownBulkActions && !!selectedRows.length) {
			props.setDownloadButton(
				<div>
					<div className="invoice-bulk-actions-acct-th">
						<button
							className="btn btn-primary"
							onClick={downloadReceiptReportCsv}
						>
							Dowload CSV receipt report
						</button>
					</div>
				</div>,
			);
		} else {
			props.setDownloadButton(null);
		}
	}, [isShownBulkActions, selectedRows]);

	useEffect(() => {
		setRows(Array.isArray(props.data) ? props.data : []);
	}, [props.data]);

	useEffect(() => {
		if (props.reload) setShowPreview(false);
	}, [props.reload]);

	useEffect(() => {
		if (showPreview) {
			setColumns([
				{ title: "Month", field: "yearMonth", width: 100 },
				{ title: "Entity", field: "entity", width: 50 },
				{ title: "Client", field: "client", width: 300 },
				{ title: "Site", field: "siteName", width: 300 },
				{
					title: "Payment Date",
					grouping: false,
					field: "paymentDate",
					width: 200,
				},
				{
					title: "Overdue Days",
					grouping: false,
					field: "agingDays",
					width: 100,
				},
				{
					title: "Actions",
					grouping: false,
					render: (dataObj) => renderActionsCol(dataObj),
					width: 100,
				},
			]);
		} else {
			setColumns(initColumns);
		}
	}, [showPreview]);

	return (
		<>
			{showPreview ? (
				<div>
					<Row>
						<Col md="7">
							<div className="table-style">
								<MaterialTable
									tableRef={matTbRef}
									columns={columns}
									data={rows}
									isLoading={props.reload}
									onSelectionChange={(rows) => setSelectedRows?.(rows)}
									options={{
										...tableOpts,
										emptyRowsWhenPaging: false,
										pageSize: 10,
										headerStyle: {
											fontSize: "1rem",
											textAlign: "center",
										},
									}}
								/>
							</div>
						</Col>
						<Col md="5" className="stick-preview">
							<ReceiptModel
								userRoles={roles}
								initForm={initForm}
								resetPreview={resetPreview}
								selectedReceipt={selectedReceipt}
							/>
						</Col>
					</Row>
				</div>
			) : (
				<div>
					<MaterialTable
						tableRef={matTbRef}
						columns={columns}
						data={rows}
						isLoading={props.reload}
						onSelectionChange={(rows) => setSelectedRows?.(rows)}
						options={{
							...tableOpts,
							exportButton: true,
							exportAllData: true,
							exportDelimiter: ",",
							exportFileName: "receipts",
							emptyRowsWhenPaging: false,
							pageSize: 10,
							headerStyle: {
								fontSize: "1rem",
								textAlign: "center",
							},
						}}
					/>
				</div>
			)}

			{showForm && (
				<div className="overlay translucent">
					<div
						id="graphContainer"
						style={{ textAlign: "center", marginTop: "25px", width: "85%" }}
						className={formClass}
					>
						<ReceiptForm
							selectedReceipt={selectedReceipt}
							resetForm={resetForm}
							notify={notify}
						/>
					</div>
				</div>
			)}
			<Noti />
		</>
	);
};

export default ReceiptTable;
